//phone edit control
//concept of implementing ngModel came from https://embed.plnkr.co/nqKUSPWb6w5QXr8a0wEu/?show=preview

import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const noop = () => { };

@Component({
    standalone: false,
    selector: 'ep-input',
    template: `
    <div style="margin:0px;padding:0px">
      <div
        style="position:relative"
        [ngStyle]="status ? { display: 'flex', 'align-items': 'center', gap: ' 0.25rem' } : {}"
      >
        <input
          *ngIf="!multiline"
          #epInput
          [mask]="ngxEditMask"
          [suffix]="suffix"
          [prefix]="prefix"
          [thousandSeparator]="thousandSeparator"
          [separatorLimit]="separatorLimit"
          [allowNegativeNumbers]="allowNegativeNumbers"
          [patterns]="customPatterns"
          [leadZero]="leadZero"
          [(ngModel)]="currentInput"
          (ngModelChange)="inputChanged($event)"
          (blur)="onBlur()"
          (focus)="onFocus(); autoSelectOnFocus() ? epInput.select() : null"
          [style.text-align]="textAlign"
          [maxLength]="maxLength"
          [size]="size || defaultSize"
          [disabled]="disabled"
          (keyup)="onKeyupEvent($event)"
          type="text"
          style="width:100%"
        />
        <ng-container *ngIf="status">
          <span *ngIf="status == 'inprogress'">
            <i class="fa fa-ep-loading fa-spin"></i>
          </span>
          <aurora-icon icon="checkmark" *ngIf="status == 'success'" [ngbTooltip]="statusTooltip"></aurora-icon>
          <aurora-icon icon="warning-fill" *ngIf="status == 'warning'" [ngbTooltip]="statusTooltip"></aurora-icon>
          <aurora-icon icon="error-fill" *ngIf="status == 'error'" [ngbTooltip]="statusTooltip"></aurora-icon>
        </ng-container>
        <textarea
          *ngIf="multiline"
          [(ngModel)]="currentInput"
          (ngModelChange)="inputChanged($event)"
          (blur)="onBlur()"
          (focus)="onFocus()"
          [style.text-align]="textAlign"
          [rows]="rows || defaultRows"
          [cols]="size || defaultSize"
          [maxLength]="maxLength"
          [disabled]="disabled"
          [spellcheck]="spellcheck"
          style="width:100%"
        >
        </textarea>
      </div>
    </div>
  `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: InputComponent, multi: true }]
})
export class InputComponent implements ControlValueAccessor {
    @ViewChild('epInput') epInput;

    @Input()
    status: string = null;
    @Input()
    statusTooltip: string = null;

    @Input()
    size: number = null;

    @Input()
    rows: number = null;

    @Input()
    spellcheck: boolean = true;

    @Input()
    disabled: boolean = false;

    @Input()
    set format(value: string) {
        //key-integers.decimals-options   integers and decimals and options can be omitted
        //text-80
        //decimal-10.2
        //money-7.2
        //percent-3.1-shift2

        let token: any[] = value.toLowerCase().split('-');
        this.formatKey = token[0];
        this.integers = null;
        this.decimals = null;

        if (this.formatKey != 'zip') {
            if ((token[1] && token[1].includes('.')) || parseInt(token[1], 10) > 0) {
                let lengthToken = token[1].split('.');
                this.integers = parseInt(lengthToken[0], 10);
                if (this.integers == 0) {
                    this.integers = null;
                }
                if (lengthToken.length > 1) {
                    this.decimals = parseInt(lengthToken[1], 10);
                }
            }
        } else {
            this.maxLength = 80; //80 is the default. USA and CAN have designated masks for formatting. Non US or CAN zips are an open 10 char input.
            if (token[1]) {
                this.countryCode = token[1].toUpperCase();
                if (this.countryCode != 'USA' && this.countryCode != 'CAN') {
                    this.maxLength = 10;
                }
            }
        }

        if (token.indexOf('uppercase') >= 0) {
            console.warn("'uppercase' option not yet implemented in ep-input component.");
        }
        if (token.indexOf('lowercase') >= 0) {
            console.warn("'lowercase' option not yet implemented in ep-input component.");
        }

        if (token.indexOf('negative') >= 0) {
            this.allowNegative = true;
        }

        switch (this.formatKey) {
            case 'phone': //(###) ###-####
                this.ngxEditMask = `(200) 000-0000`;
                this.customPatterns = {
                    '2': { pattern: new RegExp('[2-9]') },
                    '0': { pattern: new RegExp('[0-9]') }
                };
                this.textAlign = 'left';
                this.defaultSize = 14;
                this.numbersOnly = true;
                break;
            case 'zip': //#####-##
                this.textAlign = 'left';
                this.defaultSize = 9;
                switch (this.countryCode) {
                    case 'USA':
                        this.ngxEditMask = `00000||00000-0000`;
                        this.customPatterns = {
                            '0': { pattern: new RegExp('[0-9]') }
                        };
                        break;
                    case 'CAN':
                        this.ngxEditMask = `S0S 0S0`;
                        this.customPatterns = {
                            S: { pattern: new RegExp('[a-zA-Z]') },
                            '0': { pattern: new RegExp('[0-9]') }
                        };
                        break;
                    default:
                }
                break;
            case 'vin':
                this.ngxEditMask = '';
                this.numbersOnly = false;
                this.textAlign = 'left';
                this.defaultSize = 25;
                this.maxLength = 25;
                break;
            case 'ssn': //###-##-####
                this.ngxEditMask = `000-00-0000`;
                this.suffix = '';
                this.prefix = '';
                this.customPatterns = {
                    '0': { pattern: new RegExp('[0-9]') }
                };
                this.numbersOnly = true;
                this.textAlign = 'left';
                this.defaultSize = 9;
                break;
            case 'fein': //##-#######
                this.ngxEditMask = `00-0000000`;
                this.suffix = '';
                this.prefix = '';
                this.customPatterns = {
                    '0': { pattern: new RegExp('[0-9]') }
                };
                this.numbersOnly = true;
                this.textAlign = 'left';
                this.defaultSize = 9;
                break;
            case 'percent': //% sufffix   default is 3.0
                if (this.integers == null) {
                    this.integers = 3;
                }
                if (this.decimals == null) {
                    this.decimals = 0;
                }
                this.ngxEditMask = `separator.${this.decimals}`;
                this.suffix = '%';
                this.prefix = '';
                this.separatorLimit = new Array(this.integers - this.decimals + 1).join('9');
                this.allowNegativeNumbers = this.allowNegative;
                this.numbersOnly = true;
                if (token.indexOf('shift2') >= 0) {
                    this.shiftDecimal = true;
                }
                this.textAlign = 'right';
                this.defaultSize = this.integers + this.decimals;
                break;
            case 'money': //$ prefix   default is 14.2
                if (this.integers == null) {
                    this.integers = 14;
                }
                if (this.decimals == null) {
                    this.decimals = 2;
                }
                this.ngxEditMask = `separator.${this.decimals}`;
                this.prefix = '$';
                this.suffix = '';
                this.separatorLimit = new Array(this.integers - this.decimals + 1).join('9');
                this.allowNegativeNumbers = this.allowNegative;
                this.thousandSeparator = ',';
                this.numbersOnly = true;
                this.textAlign = 'right';
                this.defaultSize = this.integers + this.decimals + 1;
                break;
            case 'decimal': //no $ prefix   default is 14.2
                if (this.integers == null) {
                    this.integers = 14;
                }
                if (this.decimals == null) {
                    this.decimals = 2;
                }
                this.ngxEditMask = `separator.${this.decimals}`;
                this.separatorLimit = new Array(this.integers - this.decimals + 1).join('9');
                this.allowNegativeNumbers = this.allowNegative;
                this.thousandSeparator = ',';
                this.numbersOnly = true;
                this.textAlign = 'right';
                this.defaultSize = this.integers + this.decimals;
                break;
            case 'rate': // ####.00000    default is 9.5,  no thousands separator
                if (this.integers == null) {
                    this.integers = 9;
                }
                if (this.decimals == null) {
                    this.decimals = 5;
                }
                this.ngxEditMask = `separator.${this.decimals}`;
                this.separatorLimit = new Array(this.integers - this.decimals + 1).join('9');
                this.thousandSeparator = '';
                this.allowNegativeNumbers = this.allowNegative;
                this.numbersOnly = true;
                this.textAlign = 'right';
                this.defaultSize = this.integers + this.decimals;
                break;
            case 'number': //Whole number with thousands separator, default 10.x
                if (this.integers == null) {
                    this.integers = 10;
                }
                if (this.decimals != null) {
                    console.warn("Decimal token is ignored for 'number' formats provided to ep-input component. [" + value + ']');
                }
                this.ngxEditMask = `separator.0`;
                this.separatorLimit = new Array(this.integers + 1).join('9');
                this.allowNegativeNumbers = this.allowNegative;
                this.thousandSeparator = ',';
                this.numbersOnly = true;
                this.textAlign = 'right';
                this.defaultSize = this.integers;
                break;
            case 'integer': //Whole number without thousands separator,  default 10.x
                if (this.integers == null) {
                    this.integers = 10;
                }
                if (this.decimals != null) {
                    console.warn(
                        "Decimal token is ignored for 'integer' formats provided to ep-input component. [" + value + ']'
                    );
                }
                this.ngxEditMask = `separator.0`;
                this.separatorLimit = new Array(this.integers + 1).join('9');
                this.thousandSeparator = '';
                this.allowNegativeNumbers = this.allowNegative;
                this.numbersOnly = true;
                this.textAlign = 'right';
                this.defaultSize = this.integers;
                break;
            case 'text': //default 80 characters
                if (this.integers == null) {
                    this.integers = 80;
                    console.warn(
                        'No max size provided for \'text\' format on ep-input component. Proper use is format="text-80"'
                    );
                }
                if (this.decimals != null) {
                    console.warn("Decimal token is ignored for 'text' formats provided to ep-input component. [" + value + ']');
                }
                this.ngxEditMask = '';
                this.numbersOnly = false;
                this.textAlign = 'left';
                this.defaultSize = this.integers;
                this.maxLength = this.integers;
                break;
            case 'textbox': //default 255 characters
                if (this.integers == null) {
                    this.integers = 255;
                    console.warn(
                        'No max size provided for \'text\' format on ep-input component. Proper use is format="textarea-80"'
                    );
                }
                if (this.decimals != null) {
                    console.warn("Decimal token is ignored for 'text' formats provided to ep-input component. [" + value + ']');
                }
                this.multiline = true;
                this.ngxEditMask = '';
                this.numbersOnly = false;
                this.textAlign = 'left';
                this.defaultRows = 3;
                this.defaultSize = 130;
                this.maxLength = this.integers;
                break;
            default:
                console.error("Invalid format attribute value of '" + this.formatKey + "' provided to ep-input component.");
        }

        //override default text alignment
        if (token.indexOf('left') >= 0) {
            this.textAlign = 'left';
        }
        if (token.indexOf('right') >= 0) {
            this.textAlign = 'right';
        }
        if (token.indexOf('center') >= 0) {
            this.textAlign = 'center';
        }
    }

    @Output()
    blur = new EventEmitter();

    @Output()
    focus = new EventEmitter();

    public currentInput: string;

    //The internal data model
    private innerValue: any = '';

    private numbersOnly: boolean = false;
    public maxLength: number = 80;
    public defaultSize = 80;
    public defaultRows = 3;
    private shiftDecimal: boolean = false;
    private decimals: number = null;
    private integers: number = null;
    private allowNegative: boolean = false;
    public multiline: boolean = false;
    public formatKey: string;
    public countryCode: string = '';
    public textAlign = 'left';

    // ngx-mask properties
    public ngxEditMask: string = '';
    public customPatterns = {};
    public prefix = '';
    public suffix = '';
    public thousandSeparator = ',';
    public separatorLimit = '';
    public allowNegativeNumbers = false;
    public leadZero = true;

    //Placeholders for the callbacks which are later provided
    //by the Control Value Accessor
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    constructor(private cdRef: ChangeDetectorRef) { }

    onKeyupEvent(event: KeyboardEvent): void {
        if (this.formatKey === 'phone' && event.key === '+') {
            if (this.epInput.nativeElement.value && this.epInput.nativeElement.value.length > 0) {
                let firstChar = this.epInput.nativeElement.value[0];
                if (firstChar.indexOf('+') > -1 && this.epInput.nativeElement.value.length > 1) {
                    let onlyNums = this.epInput.nativeElement.value.replace(/\D/g, '');
                    this.currentInput = firstChar + onlyNums;
                }
            }
        }
        if (this.decimals && this.decimals > 0) {
            let value: string = this.epInput.nativeElement.value || '';
            let selectionStart: number = this.epInput.nativeElement.selectionStart;
            let selectionEnd: number = this.epInput.nativeElement.selectionEnd;
            let decimalPos = value.indexOf('.');
            if (event.key === '.') {
                if (selectionStart < decimalPos) {
                    this.epInput.nativeElement.setSelectionRange(decimalPos + 1, decimalPos + 1);
                }
            } else if (event.key >= '0' && event.key <= '9') {
                if (selectionStart === selectionEnd && selectionStart > decimalPos) {
                    // To resolve strange issue where typing 0 right after the decimal was not advancing the cursor
                    if (event.key === '0' && selectionStart - 1 === decimalPos) {
                        this.epInput.nativeElement.setSelectionRange(selectionStart + 1, selectionStart + 1);
                    }
                }
            }
        }
    }

    inputChanged(inputVal: string) {
        let newVal = '';

        if (inputVal) {
            //strip out the formatting characters   -- KEEP ONLY NUMBERS AND DECIMAL PLACE
            if (this.numbersOnly) {
                if (['phone', 'zip', 'ssn', 'fein'].includes(this.formatKey.toLowerCase())) {
                    // corrected issue with regex not stripping out hyphens in phone numbers but did not
                    //  want to change the regex for all cases so this is specific for phones
                    newVal = ('' + inputVal).replace(/\D/g, '');
                } else {
                    newVal = ('' + inputVal).replace(/[^0-9.-]/g, '');
                }
            } else {
                newVal = inputVal;
            }
            if (this.shiftDecimal) {
                newVal = (parseFloat(newVal) / 100.0).toString();
            }
        }

        this.value = newVal;
    }

    autoSelectOnFocus() {
        return ['number', 'integer', 'rate', 'decimal', 'money', 'percent'].includes(this.formatKey);
    }

    ////get accessor
    get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
        }
    }

    onBlur() {
        this.blur.emit();
        this.onTouchedCallback();
    }

    onFocus() {
        this.focus.emit();
    }

    //From ControlValueAccessor interface
    writeValue(value: any) {
        if (value !== this.innerValue) {
            this.innerValue = value;
            if (this.numbersOnly && this.decimals == 0 && value != null && value.indexOf('.') >= 0) {
                this.value = this.value.slice(0, value.indexOf('.') - 1);
            }
            if (this.shiftDecimal) {
                this.currentInput = (parseFloat(value) * 100.0).toString();
            } else {
                this.currentInput = value;
            }
            this.cdRef.detectChanges();
        }
    }

    //From ControlValueAccessor interface
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
